import {
  ChangeDetectionStrategy,
  Component,
  Inject
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef
} from '@angular/material/dialog';
import { ImageCropperComponent, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { LucideModule } from '../../modules/lucide.module';
import { DialogButtonComponent } from '../dialog-button/dialog-button.component';

/** Recorte do banner da home; quem recorta outra coisa manda largura e altura. */
const BANNER_LARGURA = 1340;
const BANNER_ALTURA = 448;

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
const ZOOM_PASSO = 0.1;

export interface BannerCropResult {
  base64: string;
  arquivo: File;
}

export interface BannerCropData {
  arquivo: File;
  largura?: number;
  altura?: number;
  /** Frase abaixo do título; quem reaproveita o recorte diz o que está sendo recortado. */
  descricao?: string;
}

@Component({
  selector: 'app-banner-crop',
  standalone: true,
  imports: [
    ImageCropperComponent,
    LucideModule,
    MatDialogClose,
    MatDialogContent,
    DialogButtonComponent
  ],
  templateUrl: './banner-crop.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './banner-crop.component.css'
})
export class BannerCropComponent {
  readonly zoomMin = ZOOM_MIN;
  readonly zoomMax = ZOOM_MAX;
  readonly zoomPasso = ZOOM_PASSO;

  readonly arquivo: File;
  readonly larguraBanner: number;
  readonly alturaBanner: number;
  readonly proporcaoBanner: number;
  readonly descricao: string;

  zoom = ZOOM_MIN;
  transform: ImageTransform = { scale: ZOOM_MIN };

  private imagemCortada: string | null = null;
  private blobCortado: Blob | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<BannerCropComponent>,
    @Inject(MAT_DIALOG_DATA) dados: File | BannerCropData
  ) {
    const entrada: BannerCropData = dados instanceof File ? { arquivo: dados } : dados;

    this.arquivo = entrada.arquivo;
    this.larguraBanner = entrada.largura ?? BANNER_LARGURA;
    this.alturaBanner = entrada.altura ?? BANNER_ALTURA;
    this.proporcaoBanner = this.larguraBanner / this.alturaBanner;
    this.descricao = entrada.descricao ?? 'Ajuste o enquadramento no formato padrão do banner.';
  }

  onImageCropped(event: ImageCroppedEvent): void {
    this.imagemCortada = event.base64 ?? null;
    this.blobCortado = event.blob ?? null;
  }

  onZoomChange(valor: number): void {
    this.zoom = this.limitarZoom(valor);
    this.aplicarZoom();
  }

  aumentarZoom(): void {
    this.onZoomChange(this.zoom + ZOOM_PASSO);
  }

  diminuirZoom(): void {
    this.onZoomChange(this.zoom - ZOOM_PASSO);
  }

  confirmar(): void {
    if (!this.imagemCortada) {
      return;
    }

    const blob = this.blobCortado ?? this.base64ParaBlob(this.imagemCortada);
    const arquivo = new File([blob], this.nomeArquivoRecorte(), { type: 'image/png' });

    const resultado: BannerCropResult = {
      base64: this.imagemCortada,
      arquivo
    };

    this.dialogRef.close(resultado);
  }

  private aplicarZoom(): void {
    this.transform = { ...this.transform, scale: this.zoom };
  }

  private nomeArquivoRecorte(): string {
    const base = this.arquivo.name.replace(/\.[^./\\]+$/, '');
    return `${base || 'banner'}.png`;
  }

  private base64ParaBlob(base64: string): Blob {
    const conteudo = base64.includes(',') ? base64.split(',')[1] : base64;
    const bytes = atob(conteudo);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) {
      buffer[i] = bytes.charCodeAt(i);
    }
    return new Blob([buffer], { type: 'image/png' });
  }

  private limitarZoom(valor: number): number {
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Number(valor)));
  }
}
